import React from 'react';
import type Viewer from 'bpmn-js/lib/Viewer';
import type Modeler from 'bpmn-js/lib/Modeler';
import { Box, Typography, Button, Tooltip } from '@mui/material';
import { useSelector } from 'react-redux';
import { RootState } from '../../../store';
import { useDiagramContext } from '../Context/DiagramContext';
import { useDiagramLayout } from '../hooks/useDiagramLayout';
import { useReportGeneration } from '../hooks/useReportGeneration';
import TaxonomyBreadcrumb from '../../TaxonomyXPLR/shared/components/TaxonomyBreadcrumb';
import { DiagramToolbar } from '../common/DiagramToolbar';
import { CloseButton } from '../common/SharedStyle';
import DiagramInfoModal from './EditModal';
import DownloadProgressSnackbar from '../../../common/components/DownloadProgressSnackbar';

const DiagramHeader: React.FC = () => {
  const {
    selectedTaxonomy,
    selectedE2E,
    selectedSubprocess,
    selectedCard,
    taxonomySubprocessName,
    taxonomySubprocessDescription,
    isProcessDiagram,
    opportunityName,
    isEditModalOpen,
    setIsEditModalOpen,
    handleClose, // ← CRITICAL: Get this from context
    handleSaveDiagram,
    isDiagramChanged,
    isEditPermission,
    isEnterpriseModalOpen,
    setIsEnterpriseModalOpen,
    showAllAiComponents,
    setShowAllAiComponents,
    onResetAll,
    activeModal,
    setActiveModal,
    aiOpportunityData,
    diagramData,
    bpmnModelerRef,
    setShowDeleteModal,
    setShowRenameModal,
    setShowDeleteIdeationModal,
    setRenameProcessName,
    isVersionViewMode,
    onRestoreVersion,
    onVersionHistoryClick,
  } = useDiagramContext();

  const { isMobile, isProcessXPLRActive, shouldShowSaveButtons } = useDiagramLayout();

  const processType = selectedCard?.processType || selectedSubprocess?.processType;
  const isCustomProcess = processType === 'custom';
  const showCloseButton = isMobile || (isProcessXPLRActive && !isCustomProcess);

  const [diagramBpmnInstance, setDiagramBpmnInstance] = React.useState<
    Modeler | Viewer | undefined
  >(undefined);

  React.useEffect(() => {
    setDiagramBpmnInstance(bpmnModelerRef.current);
  }, [bpmnModelerRef]);

  const handleCloseButton = () => {
    handleClose();
  };

  // Use the report generation hook
  const { loadingDocument, downloadProgressOpen, downloadProgress, handleDownloadReport } =
    useReportGeneration({
      diagramData,
      selectedCard,
    });

  const defaultResetAll = onResetAll || (() => window.history.back());
  const defaultResetTaxonomy = () => window.history.back();
  const defaultResetE2E = () => window.history.back();

  const isSaveDisabled = !isDiagramChanged;

  const isRestoreBlocked = useSelector((state: RootState) => state.newIdeation.is_restore_blocked);


  const restoreTooltipMsg = isRestoreBlocked
    ? 'This version cannot be restored as some opportunities are in Solutioning, Approved, or Ready to Build status.'
    : '';

  const handleToggleAiSteps = () => {
    setShowAllAiComponents(!showAllAiComponents);
  };

  const breadcrumbSelectedCard: { name: string } | null =
    selectedCard && selectedCard.name ? { name: selectedCard.name } : null;

  return (
    <Box>
      <Box
        sx={{
          position: 'sticky',
          top: 0,
          left: 0,
          right: 0,
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          alignItems: { xs: 'stretch', sm: 'center' },
          justifyContent: 'space-between',
          backgroundColor: 'rgba(244, 246, 255, 0.95)',
          pt: isProcessDiagram ? (isMobile ? 1 : 8) : 1,
          pr: 1,
          pl: 2,
          width: '100%',
          borderBottom: 'none',
          zIndex: 200,
        }}
      >
        {isProcessDiagram && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, flex: 1, pt: 1 }}>
            <Typography variant="h6" sx={{ fontSize: '24px', fontWeight: 500 }}>
              {opportunityName || ' '}
            </Typography>
          </Box>
        )}

        {(selectedTaxonomy || selectedE2E || selectedSubprocess) && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 1,
              pt: isMobile ? 1 : 2,
              pb: isMobile ? 1 : 0,
              width: '100%',
              flexWrap: 'nowrap',
            }}
          >
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <TaxonomyBreadcrumb
                selectedTaxonomy={selectedTaxonomy}
                selectedE2E={selectedE2E}
                selectedSubprocess={selectedSubprocess}
                selectedCard={breadcrumbSelectedCard}
                onResetAll={defaultResetAll}
                onResetTaxonomy={defaultResetTaxonomy}
                onResetE2E={defaultResetE2E}
                variant="diagram"
                onEdit={() => setIsEditModalOpen(true)}
                showEditIcon={isEditPermission}
                titleRight={null}
              />
              <DiagramInfoModal
                isOpen={isEditModalOpen}
                onClose={() => setIsEditModalOpen(false)}
                selectedTaxonomy={taxonomySubprocessName}
                selectedTaxonomyDescription={taxonomySubprocessDescription}
                type="taxonomy"
              />
            </Box>
            {isMobile && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  mt: '8px',
                  mr: 2,
                  alignSelf: 'flex-start',
                  flexShrink: 0,
                }}
              >
                {isEditPermission && shouldShowSaveButtons() && (
                  <Button
                    variant="contained"
                    onClick={handleSaveDiagram}
                    disabled={!isEditPermission || isSaveDisabled}
                    sx={{
                      textTransform: 'none',
                      height: 36,
                      px: 3,
                      backgroundColor: '#1A55F9',
                      color: '#FFFFFF',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    Save
                  </Button>
                )}
                {(isEditPermission || isProcessXPLRActive) && (
                  <DiagramToolbar
                    isEnterpriseModalOpen={isEnterpriseModalOpen}
                    showAllAiComponents={showAllAiComponents}
                    onEnterpriseClick={() => setIsEnterpriseModalOpen(true)}
                    onToggleAiSteps={handleToggleAiSteps}
                    selectedCard={selectedCard}
                    onResetAll={defaultResetAll}
                    onClose={handleClose}
                    onSaveDiagram={isEditPermission ? handleSaveDiagram : undefined}
                    onAiSolutionsClick={() =>
                      setActiveModal(activeModal === 'aiSolution' ? null : 'aiSolution')
                    }
                    onAddToSolutionXPLR={() => {}}
                    currentOrg={localStorage.getItem('current_organization') || ''}
                    isSaveDisabled={isSaveDisabled}
                    diagramData={diagramData}
                    aiOpportunityData={aiOpportunityData}
                    type={selectedTaxonomy ? 'taxonomy' : 'solutionxplr'}
                    bpmnInstance={diagramBpmnInstance}
                    onApplyEnterprises={() => {}}
                    onEdit={isEditPermission ? () => setIsEditModalOpen(true) : undefined}
                    onProcessInfoClick={() =>
                      setActiveModal(activeModal === 'processInfo' ? null : 'processInfo')
                    }
                    onDeleteClick={isEditPermission ? () => setShowDeleteModal(true) : undefined}
                    isProcessDiagram={isProcessDiagram}
                    onRenameProcess={
                      isEditPermission
                        ? () => {
                            setRenameProcessName(selectedCard?.name || '');
                            setShowRenameModal(true);
                          }
                        : undefined
                    }
                    onDeleteIdeation={
                      isEditPermission ? () => setShowDeleteIdeationModal(true) : undefined
                    }
                    onDownload={handleDownloadReport}
                    loadingDocument={loadingDocument}
                    isProcessXPLRActive={isProcessXPLRActive}
                  />
                )}
                {showCloseButton && <CloseButton onClick={handleCloseButton} />}
              </Box>
            )}
          </Box>
        )}

        {isVersionViewMode && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, ml: 2 }}>
            <Tooltip
              title={restoreTooltipMsg}
              arrow
              placement="bottom"
              disableHoverListener={!isRestoreBlocked}
            >
              <span>
                <Button
                  variant="contained"
                  disabled={isRestoreBlocked}
                  onClick={isRestoreBlocked ? undefined : onRestoreVersion}
                  sx={{
                    textTransform: 'none',
                    height: 30,
                    px: 3,
                    backgroundColor: isRestoreBlocked ? '#B0BEC5' : '#1A55F9',
                    color: '#FFFFFF',
                    cursor: isRestoreBlocked ? 'not-allowed' : 'pointer',
                    '&:hover': {
                      backgroundColor: isRestoreBlocked ? '#B0BEC5' : '#0f3fd1',
                    },
                    '&.Mui-disabled': {
                      backgroundColor: '#B0BEC5',
                      color: '#FFFFFF',
                    },
                  }}
                >
                  Restore
                </Button>
              </span>
            </Tooltip>
          </Box>
        )}

        {!isMobile && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-start',
              alignItems: 'center',
              ml: 'auto',
              pr: 3,
              gap: 2,
            }}
          >
            {isEditPermission && shouldShowSaveButtons() && (
              <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
                {/* CRITICAL FIX: Discard button should trigger handleClose */}
                <Button
                  variant="outlined"
                  onClick={handleClose}
                  sx={{
                    textTransform: 'none',
                    height: 30,
                    px: 2,
                    borderColor: '#1A55F9',
                    color: '#1A55F9',
                  }}
                >
                  Cancel
                </Button>

                <Button
                  variant="contained"
                  onClick={isEditPermission ? handleSaveDiagram : undefined}
                  disabled={!isEditPermission || isSaveDisabled}
                  sx={{
                    textTransform: 'none',
                    height: 30,
                    px: 3,
                    backgroundColor: '#1A55F9',
                    color: '#FFFFFF',
                  }}
                >
                  Save
                </Button>
              </Box>
            )}
            {(isEditPermission || isProcessXPLRActive || isVersionViewMode) && (
              <DiagramToolbar
                isEnterpriseModalOpen={isEnterpriseModalOpen}
                showAllAiComponents={showAllAiComponents}
                onEnterpriseClick={() => setIsEnterpriseModalOpen(true)}
                onToggleAiSteps={handleToggleAiSteps}
                selectedCard={selectedCard}
                onResetAll={defaultResetAll}
                onClose={handleClose}
                onSaveDiagram={isEditPermission ? handleSaveDiagram : undefined}
                onAiSolutionsClick={() =>
                  setActiveModal(activeModal === 'aiSolution' ? null : 'aiSolution')
                }
                onAddToSolutionXPLR={() => {}}
                currentOrg={localStorage.getItem('current_organization') || ''}
                isSaveDisabled={isSaveDisabled}
                diagramData={diagramData}
                aiOpportunityData={aiOpportunityData}
                type={selectedTaxonomy ? 'taxonomy' : 'solutionxplr'}
                bpmnInstance={diagramBpmnInstance}
                onApplyEnterprises={() => {}}
                onEdit={isEditPermission ? () => setIsEditModalOpen(true) : undefined}
                onProcessInfoClick={() =>
                  setActiveModal(activeModal === 'processInfo' ? null : 'processInfo')
                }
                onDeleteClick={isEditPermission ? () => setShowDeleteModal(true) : undefined}
                isProcessDiagram={isProcessDiagram}
                onRenameProcess={
                  isEditPermission
                    ? () => {
                        setRenameProcessName(selectedCard?.name || '');
                        setShowRenameModal(true);
                      }
                    : undefined
                }
                onDeleteIdeation={
                  isEditPermission ? () => setShowDeleteIdeationModal(true) : undefined
                }
                onDownload={handleDownloadReport}
                loadingDocument={loadingDocument}
                isProcessXPLRActive={isProcessXPLRActive}
                onVersionHistoryClick={onVersionHistoryClick}
                  isVersionViewMode={isVersionViewMode}
              />
            )}
            {showCloseButton && <CloseButton onClick={handleCloseButton} />}
          </Box>
        )}
      </Box>

      <DownloadProgressSnackbar open={downloadProgressOpen} progress={downloadProgress} />
    </Box>
  );
};

export default DiagramHeader;
